/*
  Size selection state for the main product form, owned by <product-info>.

  Shopify always resolves a selection to a complete variant and fills any missing option with its first
  value (usually XS). This keeps the customer's actual choice on the client instead:
  - no size is selected until the customer picks one (or arrives with ?variant=),
  - option requests always resolve to a real variant so media and prices render,
  - the submit button and URL are derived from one state.
*/
window.NcmVariantSelection ??= class NcmVariantSelection {
  constructor(productInfo, dataElement) {
    this.productInfo = productInfo;
    const data = JSON.parse(dataElement.textContent);
    this.variants = data.variants;
    this.optionNames = data.options;
    this.sizeIndex = data.sizeIndex;
    this.colorIndex = data.colorIndex;
    this.strings = NcmVariantSelection.decodeStrings(data.strings);
    this.sizeChosen = data.explicitVariant;
    this.state = null;

    this.abortController = new AbortController();
    this.productInfo.addEventListener('submit', this.onSubmit.bind(this), {
      capture: true,
      signal: this.abortController.signal,
    });
    this.productInfo.addPreProcessCallback((node) => this.normalize(node));

    this.selection = this.readSelection(this.productInfo.variantSelectors);
    if (!this.sizeChosen) this.selection[this.sizeIndex] = null;
    this.normalize(this.productInfo.variantSelectors);
    this.apply(this.productInfo);
  }

  // Translations arrive HTML-escaped (e.g. &#39;) but are written as text.
  static decodeStrings(strings) {
    const decoder = document.createElement('textarea');
    return Object.fromEntries(
      Object.entries(strings).map(([key, value]) => {
        decoder.innerHTML = value;
        return [key, decoder.value];
      })
    );
  }

  disconnect() {
    this.abortController.abort();
  }

  get sectionId() {
    return this.productInfo.dataset.section;
  }

  get submitButton() {
    return this.productInfo.querySelector(`#ProductSubmitButton-${this.sectionId}`);
  }

  getFieldsets(root) {
    return root ? [...root.querySelectorAll('.product-form__input')] : [];
  }

  // Size/Color are rendered Size-then-Color for presentation, but variant.options
  // stay in Shopify catalog order. Map each fieldset back to that catalog index.
  fieldsetIndex(fieldset) {
    if (!fieldset) return -1;
    if (fieldset.classList.contains('product-form__input--size')) return this.sizeIndex;
    if (fieldset.classList.contains('ncm-color-swatches')) return this.colorIndex;
    const named = fieldset.querySelector('input[name], select[name]');
    const position = named?.name?.match(/-(\d+)\s*$/);
    if (position) return Number(position[1]) - 1;
    return this.getFieldsets(fieldset.parentElement).indexOf(fieldset);
  }

  fieldsetByIndex(root, index) {
    return this.getFieldsets(root).find((fieldset) => this.fieldsetIndex(fieldset) === index);
  }

  readSelection(root) {
    const selection = this.optionNames.map(() => null);
    this.getFieldsets(root).forEach((fieldset) => {
      const index = this.fieldsetIndex(fieldset);
      if (index < 0) return;
      selection[index] =
        fieldset.querySelector('input[type="radio"]:checked')?.value ??
        fieldset.querySelector('select')?.value ??
        null;
    });
    return selection;
  }

  matches(variant, selection, ignoreIndex = -1) {
    return variant.options.every(
      (value, index) => index === ignoreIndex || selection[index] == null || value === selection[index]
    );
  }

  findVariant(selection) {
    return this.variants.find((variant) => variant.options.every((value, index) => value === selection[index]));
  }

  // Shown availability: a value is crossed out when it can't be bought with the other selected options
  // (sizes for the chosen color, colors for the chosen size).
  isValueAvailable(index, value) {
    return this.variants.some(
      (variant) => variant.available && variant.options[index] === value && this.matches(variant, this.selection, index)
    );
  }

  isValueInStock(index, value) {
    return this.variants.some((variant) => variant.available && variant.options[index] === value);
  }

  /** Called by product-info before requesting the section; returns the option value ids to request. */
  prepareRequest(target) {
    const fieldsets = this.getFieldsets(this.productInfo.variantSelectors);
    const changedIndex = this.fieldsetIndex(fieldsets.find((fieldset) => fieldset.contains(target)));
    const selection = this.readSelection(this.productInfo.variantSelectors);

    if (changedIndex === this.sizeIndex) this.sizeChosen = true;
    if (!this.sizeChosen) selection[this.sizeIndex] = null;
    this.selection = selection;

    // Without a purchasable size, render an available variant of the chosen color so the media and
    // price shown belong to that color (Shopify would otherwise fill in XS or another color).
    const request = [...selection];
    if (!this.findVariant(request)?.available) {
      const candidates = this.variants.filter((variant) => this.matches(variant, request, this.sizeIndex));
      const representative = candidates.find((variant) => variant.available) || candidates[0];
      if (representative) request[this.sizeIndex] = representative.options[this.sizeIndex];
    }

    return request
      .map((value, index) => {
        if (value == null) return null;
        const fieldset = this.fieldsetByIndex(this.productInfo.variantSelectors, index);
        const input = [...(fieldset?.querySelectorAll('input, option') || [])].find((element) => element.value === value);
        return input?.dataset.optionValueId;
      })
      .filter(Boolean);
  }

  /** Applies the client selection and availability to a variant-selects node (live or about to be inserted). */
  normalize(node) {
    const variantSelects = node?.matches?.('variant-selects') ? node : node?.querySelector?.('variant-selects');
    if (!variantSelects) return;

    this.getFieldsets(variantSelects).forEach((fieldset) => {
      const index = this.fieldsetIndex(fieldset);
      const isColor = fieldset.classList.contains('ncm-color-swatches');
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const checked = input.value === this.selection[index];
        input.toggleAttribute('checked', checked);
        input.checked = checked;

        const available = this.isValueAvailable(index, input.value);
        input.classList.toggle('disabled', !available);
        if (isColor) {
          fieldset
            .querySelector(`label[for="${CSS.escape(input.id)}"]`)
            ?.classList.toggle('ncm-color-swatch-label--oos', !available);
        }
      });

      const selectedValue = fieldset.querySelector('[data-selected-value]');
      if (selectedValue && !selectedValue.querySelector('.swatch')) selectedValue.textContent = this.selection[index] ?? '';
    });
  }

  computeState() {
    if (!this.variants.some((variant) => variant.available)) return { state: 'sold-out' };

    const unavailableIndex = this.selection.findIndex(
      (value, index) => index !== this.sizeIndex && value != null && !this.isValueInStock(index, value)
    );
    if (unavailableIndex > -1) return { state: 'option-unavailable', index: unavailableIndex };

    if (this.selection[this.sizeIndex] == null) return { state: 'choose-size' };

    const variant = this.findVariant(this.selection);
    if (!variant?.available) return { state: 'size-unavailable' };
    return { state: 'ready', variant };
  }

  /** Called after every option render (and once on load). */
  apply() {
    const result = this.computeState();
    this.state = result.state;
    this.productInfo.dataset.selectionState = result.state;

    this.updateButton(result);
    if (result.state !== 'ready' && result.state !== 'sold-out') this.clearUrlVariant();

    this.productInfo.dispatchEvent(
      new CustomEvent('ncm:selection-change', { bubbles: true, detail: { state: result.state } })
    );
  }

  updateButton({ state, index, variant }) {
    const button = this.submitButton;
    const label = button?.querySelector('span');
    const idInput = this.productInfo.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
    if (!button || !label) return;

    if (state === 'sold-out') {
      button.setAttribute('disabled', 'disabled');
      label.textContent = window.variantStrings.soldOut;
      delete button.dataset.selectionAction;
      return;
    }

    button.removeAttribute('disabled');
    if (state === 'ready') {
      label.textContent = window.variantStrings.addToCart;
      if (idInput) idInput.value = variant.id;
      delete button.dataset.selectionAction;
      return;
    }

    if (idInput) idInput.value = '';
    if (state === 'option-unavailable') {
      button.dataset.selectionAction = String(index);
      label.textContent = index === this.colorIndex ? this.strings.chooseAnotherColor : this.strings.chooseAnotherOption;
    } else {
      button.dataset.selectionAction = String(this.sizeIndex);
      label.textContent = state === 'choose-size' ? this.strings.chooseSize : this.strings.chooseAnotherSize;
    }
  }

  clearUrlVariant() {
    if (this.productInfo.dataset.updateUrl === 'false') return;
    if (!new URLSearchParams(window.location.search).has('variant')) return;
    window.history.replaceState({}, '', this.productInfo.dataset.url);
  }

  onSubmit(event) {
    // form.id is shadowed by the form's <input name="id">.
    if (event.target.getAttribute('id') !== `product-form-${this.sectionId}`) return;
    if (this.state === 'ready' || this.state === 'sold-out') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.reveal(Number(this.submitButton?.dataset.selectionAction ?? this.sizeIndex));
  }

  // One window scroll, only when the options are not already in view. The visible area excludes the
  // sticky header and the mobile sticky add-to-cart bar.
  reveal(index) {
    const fieldset = this.fieldsetByIndex(this.productInfo.variantSelectors, index);
    if (!fieldset) return;

    const input =
      fieldset.querySelector('input[type="radio"]:checked:not(.disabled)') ||
      fieldset.querySelector('input[type="radio"]:not(.disabled), select');
    input?.focus({ preventScroll: true });

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const modal = this.productInfo.closest('quick-add-modal');
    const scroller = modal?.querySelector('.ncm-qa__options') || modal?.querySelector('.quick-add-modal__content-info');

    if (scroller) {
      const fieldRect = fieldset.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();
      if (fieldRect.top < scrollerRect.top || fieldRect.bottom > scrollerRect.bottom) {
        scroller.scrollTo({
          top: scroller.scrollTop + fieldRect.top - scrollerRect.top - 12,
          behavior: reducedMotion ? 'instant' : 'smooth',
        });
      }
    } else {
      const header = document.querySelector('.section-header');
      const top = header && getComputedStyle(header).position !== 'static' ? Math.max(header.getBoundingClientRect().bottom, 0) : 0;
      const stickyBar = document.querySelector('ncm-sticky-atc[data-visible="true"]');
      const bottom = window.innerHeight - (stickyBar?.offsetHeight ?? 0);
      const rect = fieldset.getBoundingClientRect();

      if (rect.top < top || rect.bottom > bottom) {
        const offset = Math.max((bottom - top - rect.height) / 2, 0);
        window.scrollTo({ top: window.scrollY + rect.top - top - offset, behavior: reducedMotion ? 'instant' : 'smooth' });
      }
    }

    fieldset.classList.remove('ncm-selection-attention');
    void fieldset.offsetWidth;
    fieldset.classList.add('ncm-selection-attention');
    clearTimeout(this.attentionTimer);
    this.attentionTimer = setTimeout(() => fieldset.classList.remove('ncm-selection-attention'), 1600);
  }
};

document.querySelectorAll('product-info').forEach((productInfo) => productInfo.initSelectionState?.());
